'use client';

import {
  Button,
  InfoCard,
  Input,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '@goodneighbor/ui';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import styles from './partnerLoginForm.module.scss';
import { usePartnerLogin } from '@/hooks/usePartnerLogin';
import Link from 'next/link';

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional()
});

type LoginValues = z.infer<typeof loginSchema>;

export default function PartnerLoginForm() {
  const { signInMutation, handleSubmit } = usePartnerLogin();

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false
    }
  });

  const onSubmit = (data: LoginValues) => {
    handleSubmit({
      email: data.email,
      password: data.password
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className={styles.form}>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Email"
                  type="email"
                  hideIcon
                  wrapperClassName={styles.inputWrapper}
                  inputClassName={styles.inputField}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  type="password"
                  placeholder="Password"
                  wrapperClassName={styles.inputWrapper}
                  inputClassName={styles.inputField}
                  showPasswordToggle
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className={styles.rememberMeContainer}>
          {/* TODO: Add remember me post v1 */}
          {/* <FormField
            control={form.control}
            name="rememberMe"
            render={({ field }) => (
              <FormItem className={styles.checkboxItem}>
                <FormControl>
                  <Checkbox
                    isSelected={field.value}
                    onChange={field.onChange}
                    isDisabled
                    label="Remember for 30 days"
                    className={styles.checkbox}
                  />
                </FormControl>
              </FormItem>
            )}
          /> */}

          <Link
            className={styles.forgotPasswordLink}
            href="/partner/forgot-password"
          >
            Forgot password
          </Link>
        </div>

        <Button
          className={styles.submitButton}
          type="submit"
          size="lg"
          isLoading={signInMutation.isPending}
        >
          Sign in
        </Button>

        {signInMutation.isError && (
          <InfoCard severity="error">
            {(signInMutation.error as { message: string })?.message}
          </InfoCard>
        )}
      </form>
    </Form>
  );
}
